import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { KycSmartDocumentDto } from '../../../../../services/kycDocuments.service';
import { discrepancyMessage } from './kycDiscrepancy';

interface Props {
  doc: KycSmartDocumentDto;
  onClose: () => void;
  onConfirm?: (id: number, corrections?: Record<string, string>) => Promise<void> | void;
  /** Plan P8: re-run the AuthBridge government-source verification ("Retry"). */
  onVerify?: (id: number) => Promise<void> | void;
}

/** G-14: the ONLY fields the investor may correct (mirrors the BE allow-list). */
const NAME_CORRECTABLE: { name: string; label: string }[] = [
  { name: 'first_name', label: 'First name' },
  { name: 'middle_name', label: 'Middle name' },
  { name: 'last_name', label: 'Last name' },
  { name: 'father_first_name', label: "Father's first name" },
  { name: 'father_middle_name', label: "Father's middle name" },
  { name: 'father_last_name', label: "Father's last name" },
];

/** Plan P10: cheque readings the investor may correct (BE allow-list extended the same way). */
const CHEQUE_CORRECTABLE: { name: string; label: string }[] = [
  { name: 'account_number', label: 'Account number' },
  { name: 'ifsc_code', label: 'IFSC code' },
  { name: 'account_holder_name', label: 'Account holder name' },
];

/** Plan P9: verification badge per outcome. */
const VERIFICATION_BADGE: Record<string, { cls: string; label: (svc: string) => string }> = {
  VERIFIED: { cls: 'bg-success', label: (svc) => (svc === 'PASSPORT' ? 'Verified with Passport Seva' : 'Verified with Income Tax Dept') },
  MISMATCH: { cls: 'bg-warning text-dark', label: () => 'Name differs from government record' },
  INVALID: { cls: 'bg-danger', label: (svc) => (svc === 'PASSPORT' ? 'Passport not valid at source' : 'PAN not valid at source') },
  NOT_FOUND: { cls: 'bg-danger', label: () => 'Not found in government record' },
  UNAVAILABLE: { cls: 'bg-secondary', label: () => 'Verification pending' },
  LIMIT: { cls: 'bg-secondary', label: () => 'Verification pending' },
  NOT_CONSENTED: { cls: 'bg-light text-dark border', label: () => 'Verification not enabled' },
};

const FIELD_LABELS: Record<string, string> = {
  'user_personal_information.investor_first_name': 'First name',
  'user_personal_information.investor_middle_name': 'Middle name',
  'user_personal_information.investor_last_name': 'Last name',
  'user_personal_information.investor_gender': 'Gender',
  'user_personal_information.user_dob': 'Date of birth',
  'user_personal_information.user_pan_no': 'PAN number',
  'user_personal_information.user_aadhar_no': 'Aadhaar number',
};

/** OCR extract names (snake_case, per document type) → investor-friendly labels. */
const OCR_FIELD_LABELS: Record<string, string> = {
  full_name: 'Full name',
  first_name: 'First name',
  middle_name: 'Middle name',
  last_name: 'Last name',
  father_name: "Father's name",
  father_first_name: "Father's first name",
  father_middle_name: "Father's middle name",
  father_last_name: "Father's last name",
  mother_name: "Mother's name",
  spouse_name: "Spouse's name",
  date_of_birth: 'Date of birth',
  dob: 'Date of birth',
  gender: 'Gender',
  pan_number: 'PAN number',
  aadhaar_number: 'Aadhaar number',
  aadhar_number: 'Aadhaar number',
  passport_number: 'Passport number',
  oci_card_no: 'OCI card number',
  oci_number: 'OCI card number',
  document_number: 'Document number',
  place_of_birth: 'Place of birth',
  place_of_issue: 'Place of issue',
  date_of_issue: 'Date of issue',
  date_of_expiry: 'Expiry date',
  expiry_date: 'Expiry date',
  nationality: 'Nationality',
  address: 'Address',
  signature_present: 'Signature present',
  photo_present: 'Photo present',
  // Aadhaar / Passport / OCI / address-proof extras (every name ever stored in
  // kyc_document_fields is either here or handled by the fallback below).
  mother_first_name: "Mother's first name",
  mother_middle_name: "Mother's middle name",
  mother_last_name: "Mother's last name",
  birth_date: 'Date of birth',
  given_name: 'Given name',
  surname: 'Surname',
  issue_date: 'Date of issue',
  valid_until: 'Valid until',
  enrolment_number: 'Enrolment number',
  file_number: 'File number',
  old_passport_number: 'Old passport number',
  issuing_authority: 'Issuing authority',
  country_of_residence: 'Country of residence',
  country_code: 'Country code',
  mobile: 'Mobile number',
  mobile_number: 'Mobile number',
  pin_code: 'PIN code',
  // AuthBridge OCR (plan P3): PAN / passport / cheque extras
  account_number: 'Account number',
  ifsc_code: 'IFSC code',
  micr_code: 'MICR code',
  bank_name: 'Bank name',
  account_holder_name: 'Account holder name',
  account_type: 'Account type',
  cheque_number: 'Cheque number',
  age: 'Age',
  minor: 'Minor',
  is_scanned: 'Scanned copy',
  scan_type: 'Scan type',
  passport_type: 'Passport type',
  mrz_code1: 'MRZ line 1',
  mrz_code2: 'MRZ line 2',
  country_of_issue: 'Country of issue',
  postal_code: 'Postal code',
  zip_code: 'ZIP code',
  present_address: 'Present address',
  permanent_address: 'Permanent address',
  address_line1: 'Address line 1',
  address_line2: 'Address line 2',
  address_line3: 'Address line 3',
  sub_district: 'Sub-district',
  print_date: 'Print date',
  vid: 'VID',
};

/** Words that stay uppercase when we fall back to Title-Casing an unknown name. */
const LABEL_ACRONYMS = new Set(['pan', 'oci', 'dob', 'id', 'uid', 'vid', 'nri', 'kyc', 'mrz']);

function prettyFieldName(raw: string): string {
  if (FIELD_LABELS[raw]) return FIELD_LABELS[raw];
  const bare = raw.includes('.') ? raw.slice(raw.lastIndexOf('.') + 1) : raw;
  if (OCR_FIELD_LABELS[bare]) return OCR_FIELD_LABELS[bare];
  // Fallback: snake_case → sentence case, acronyms uppercased.
  return bare
    .split('_')
    .filter(Boolean)
    .map((w, i) => LABEL_ACRONYMS.has(w) ? w.toUpperCase() : (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export const ExtractedFieldsReview: React.FC<Props> = ({ doc, onClose, onConfirm, onVerify }) => {
  const [submitting, setSubmitting] = useState<'confirm' | null>(null);
  // G-14 Correct mode: exactly the six name fields become editable; everything else read-only.
  const [correcting, setCorrecting] = useState(false);
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [verifying, setVerifying] = useState(false);
  const verification = doc.verification || null;
  // Once a government source confirmed the name on the card, the investor's own name fields are
  // not correctable any more (the API refuses it too, UAT 2026-10-23); father's name stays editable.
  const nameLocked = verification?.outcome === 'VERIFIED' && !!verification?.nameAtSource;
  const CORRECTABLE = doc.documentType === 'CANCELLED_CHEQUE'
    ? CHEQUE_CORRECTABLE
    : nameLocked
      ? NAME_CORRECTABLE.filter((f) => f.name.startsWith('father_'))
      : NAME_CORRECTABLE;
  const verifiedName = nameLocked ? verification!.nameAtSource : null;
  const verificationBadge = verification ? VERIFICATION_BADGE[verification.outcome] : null;
  // NOT_CONSENTED is not retryable from here — consent is enabled from My Documents.
  const canRetryVerification = !!onVerify && !!doc.id && !!verification
    && ['UNAVAILABLE', 'LIMIT'].includes(verification.outcome);
  const fieldValue = (name: string): string =>
    corrections[name] ?? doc.fields.find((f) => f.fieldName === name)?.fieldValue ?? '';
  const blocking = doc.discrepancies.filter((d) => d.severity === 'BLOCKING');
  const warnings = doc.discrepancies.filter(
    (d) => d.severity === 'WARNING' && d.canonicalSource !== 'OCR_INFRA'
  );
  // INFO rows (plan P4/P7) are notes only: never a flag, never hide Accept.
  const ocrInfra = doc.discrepancies.filter((d) => d.canonicalSource === 'OCR_INFRA' && d.severity !== 'INFO');
  const infoNotes = doc.discrepancies.filter((d) => d.severity === 'INFO');

  // Legacy-path document (user 2026-08-26): uploaded via the journey's KYC documents page
  // (no OCR ever ran) and already accepted by the service provider. There is nothing to
  // review, correct, or accept — the OCR decision buttons and the "re-upload a clearer
  // scan" hint would only mislead. Shown as an informational state with Close.
  const approvedWithoutOcr = doc.fields.length === 0
    && ['approved', 'submitted'].includes((doc.status || '').toLowerCase());

  // Deciding only offered when the upload actually made it through
  // (blocking = transaction rolled back, nothing to confirm against).
  const canDecide = !!onConfirm && blocking.length === 0 && !!doc.id && !approvedWithoutOcr;
  const alreadyConfirmed = !!doc.confirmedAt;

  // User rule 2026-09-04: while a DISCREPANCY or OCR failure is flagged, the plain
  // "Accept" must NOT be offered — accepting flagged data as-is is never right. The
  // resolution paths stay: "Correct details" (a correction that makes the document match
  // revalidates it to VALID server-side), Skip, or re-upload from the card.
  const hasFlags = warnings.length > 0
    || ocrInfra.length > 0
    || doc.validationStatus === 'DISCREPANCY'
    || doc.validationStatus === 'OCR_FAILED';

  const handleConfirm = async () => {
    if (!onConfirm || !doc.id) return;
    if (correcting) {
      // Only send fields the investor actually changed vs the extracted value.
      const changed: Record<string, string> = {};
      for (const { name } of CORRECTABLE) {
        const original = doc.fields.find((f) => f.fieldName === name)?.fieldValue ?? '';
        const value = corrections[name];
        if (value !== undefined && value.trim() !== (original || '').trim()) {
          changed[name] = value.trim();
        }
      }
      setSubmitting('confirm');
      try { await onConfirm(doc.id, changed); } finally { setSubmitting(null); }
      return;
    }
    setSubmitting('confirm');
    try { await onConfirm(doc.id); } finally { setSubmitting(null); }
  };
  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          Extracted details - {doc.documentType}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="d-flex gap-2 flex-wrap">
            {doc.validationStatus && (
              <span
                className={`badge ${
                  doc.validationStatus === 'VALID'
                    ? 'bg-success'
                    : doc.validationStatus === 'DISCREPANCY' || doc.validationStatus === 'OCR_FAILED'
                    ? 'bg-warning text-dark'
                    : doc.validationStatus === 'EXPIRED'
                    ? 'bg-danger'
                    : 'bg-secondary'
                }`}
              >
                {doc.validationStatus}
              </span>
            )}
            {doc.expiryDate && (
              <span className="badge bg-light text-dark">Expires {doc.expiryDate}</span>
            )}
            {verificationBadge && verification && (
              <span className={`badge ${verificationBadge.cls}`}>
                {verificationBadge.label(verification.service)}
              </span>
            )}
            {canRetryVerification && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1"
                disabled={verifying}
                onClick={async () => {
                  if (!onVerify || !doc.id) return;
                  setVerifying(true);
                  try { await onVerify(doc.id); } finally { setVerifying(false); }
                }}
              >
                {verifying ? 'Checking...' : 'Retry verification'}
              </button>
            )}
            {/* OCR confidence deliberately not shown (user 2026-09-04) — it's an
                internal quality metric; the blur threshold acts on it server-side. */}
          </div>
        </div>

        {verification && ['VERIFIED', 'MISMATCH', 'INVALID', 'NOT_FOUND'].includes(verification.outcome) && (
          <div className="border rounded p-2 mb-3 bg-light">
            <h6 className="mb-2 small text-uppercase text-muted">Government record</h6>
            <table className="table table-sm mb-0">
              <tbody>
                {verification.nameAtSource && (
                  <tr><td className="text-muted">Name on record</td><td>{verification.nameAtSource}</td></tr>
                )}
                {verification.sourceStatus && (
                  <tr><td className="text-muted">Status</td><td>{verification.sourceStatus}</td></tr>
                )}
                {verification.nameMatch != null && (
                  <tr><td className="text-muted">Name matches document</td><td>{verification.nameMatch ? 'Yes' : 'No (record name applied)'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {infoNotes.length > 0 && (
          <ul className="small text-muted mb-3">
            {infoNotes.map((d) => (
              <li key={d.id}>{discrepancyMessage(d, doc.documentType, verifiedName)}</li>
            ))}
          </ul>
        )}

        {ocrInfra.length > 0 && (
          <div className="alert alert-warning">
            <strong>OCR could not read this document</strong>
            <ul className="mb-0 small">
              {ocrInfra.map((d) => (
                <li key={d.id}>{d.observedValue}</li>
              ))}
            </ul>
            <div className="small mt-2">
              Please make sure the document reader service is running and try again.
              If the problem persists, contact support with the time and document type.
            </div>
          </div>
        )}

        {blocking.length > 0 && (
          <div className="alert alert-danger">
            <strong>Upload blocked</strong>
            <ul className="mb-0 small">
              {blocking.map((d) => (
                <li key={d.id}>{discrepancyMessage(d, doc.documentType, verifiedName)}</li>
              ))}
            </ul>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="alert alert-warning">
            <strong>Review flags</strong>
            <ul className="mb-0 small">
              {warnings.map((d) => (
                <li key={d.id}>{discrepancyMessage(d, doc.documentType, verifiedName)}</li>
              ))}
            </ul>
          </div>
        )}

        {doc.profileConflicts && doc.profileConflicts.length > 0 && (
          <div className="alert alert-info">
            <strong>Some fields were not imported into your profile</strong>
            <ul className="mb-0 small">
              {doc.profileConflicts.map((c) => (
                <li key={c.targetField}>
                  <span className="text-muted">{prettyFieldName(c.targetField)}:</span>{' '}
                  your profile says "<strong>{c.existingValue}</strong>",
                  document says "<strong>{c.documentValue}</strong>".
                  {c.reason ? ` ${c.reason}.` : ''}
                </li>
              ))}
            </ul>
            <div className="small mt-2">
              Please open your profile and update these values if the document is correct.
            </div>
          </div>
        )}

        {correcting && (
          <div className="border rounded p-3 mb-2 bg-light">
            <h6 className="mb-2">Correct your details</h6>
            <p className="small text-muted mb-2">
              {doc.documentType === 'CANCELLED_CHEQUE'
                ? 'Only the account number, IFSC and account holder name can be corrected. The original extracted values are kept for audit.'
                : 'Only the name fields can be corrected. The original extracted values are kept for audit.'}
            </p>
            <div className="row g-2">
              {CORRECTABLE.map(({ name, label }) => (
                <div className="col-6" key={name}>
                  <label className="form-label small mb-0" htmlFor={`correct-${name}`}>{label}</label>
                  <input
                    id={`correct-${name}`}
                    className="form-control form-control-sm"
                    value={fieldValue(name)}
                    // Name fields are UPPERCASE everywhere (registration, OCR extraction,
                    // profile) — store the corrected value uppercased too, not just styled,
                    // so what is saved matches what every other surface holds.
                    style={{ textTransform: 'uppercase' }}
                    onChange={(e) => setCorrections((prev) => ({ ...prev, [name]: e.target.value.toUpperCase() }))}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <h6 className="mt-3 mb-2">Extracted fields</h6>
        {doc.fields.length === 0 ? (
          approvedWithoutOcr ? (
            <p className="text-muted small mb-0">
              This document was uploaded through your journey's KYC documents page and has been
              accepted by your service provider — there are no OCR-extracted fields to review.
            </p>
          ) : (
            <p className="text-muted small mb-0">No fields were extracted. You may need to re-upload a clearer scan.</p>
          )
        ) : (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {doc.fields.filter((f) => f.fieldName !== 'raw_text').map((f, i) => (
                  <tr key={`${f.fieldName}-${i}`}>
                    <td>{prettyFieldName(f.fieldName)}</td>
                    <td>{f.fieldValue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {canDecide && !alreadyConfirmed ? (
          /* G-14: exactly the three outcomes — Skip (defer), Correct, Accept.
             Skip closes without deciding; the card stays "Awaiting your
             confirmation" and this review reopens any time. No reject here:
             a wrong/blurry document is handled by Re-upload on the card. */
          <>
            {submitting === null && (
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Skip for now
              </button>
            )}
            {submitting === null && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setCorrecting((c) => !c)}
              >
                {correcting ? 'Cancel correction' : 'Correct details'}
              </button>
            )}
            {/* Flagged documents (discrepancy / OCR failure) hide the plain Accept —
                only "Save corrections & accept" (while correcting) can confirm them. */}
            {(!hasFlags || correcting) && (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleConfirm}
                disabled={submitting !== null}
              >
                {submitting === 'confirm' ? 'Saving...'
                  : correcting ? 'Save corrections & accept' : 'Accept'}
              </button>
            )}
          </>
        ) : (
          <>
            {alreadyConfirmed && (
              <span className="text-success small me-2">Confirmed {doc.confirmedAt?.slice(0, 10)}</span>
            )}
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Close
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ExtractedFieldsReview;
